/**
 * /guides/wedding-website-vs-digital-wedding-invitation
 *
 * "Wedding Website vs Digital Wedding Invitation: What's the Difference?" — the
 * twelfth guide, and the first one that sits directly under the invitation
 * product rather than under seating, RSVP or planning. It answers the question
 * a couple asks BEFORE they choose anything: two categories of product are
 * being sold to them under names that half the market uses interchangeably,
 * and they cannot tell whether they are looking at one decision or two.
 *
 * ── What the SERP actually contains, and the gap it leaves ────────────────
 * Almost nothing answers the query as written. The results divide into four
 * shapes, none of which is this article:
 *
 *   1. "digital vs PRINT invitations" — a different question (the format of the
 *      invitation), answered as though it were this one.
 *   2. "wedding website vs PAPER invitations" — the dominant shape, and the
 *      most useful of the four, but it compares a website against a printed
 *      card. A couple choosing between two DIGITAL things is not served by it.
 *   3. platform listicles — "best digital invitation apps 2026" — which answer
 *      "which product" rather than "which category".
 *   4. provider explainers, which describe their own bundle and end, without
 *      exception, at "you need both". That answer is sold by companies that
 *      sell both, and it is not always true.
 *
 * So the differentiator here is not tone or depth, it is the comparison
 * itself: two digital experiences, compared by FUNCTION rather than by name,
 * with a real "one may be enough" branch that the commercial results do not
 * offer. The research also supplied the three-way vocabulary the article is
 * built on — static file, hosted invitation page, reference site — which is
 * genuinely how the market divides and is nowhere stated plainly.
 *
 * ── The rule the whole article is written under ───────────────────────────
 * NO TERMINOLOGY CLAIM IS ABSOLUTE. Providers disagree with each other, and a
 * reader who memorises a vocabulary rule here will be wrong on the next site
 * they open. Every definition is therefore hedged ("usually", "often", "in
 * most cases", "depending on the product") and the article says outright,
 * twice, that the names are a starting point rather than a specification. The
 * functional distinction — what guests need to KNOW versus what they need to
 * DO, and how many places that lives in — is what the reader is meant to leave
 * with. Removing the hedges would make the piece confidently wrong.
 *
 * The second rule: no universal advice. Not "every couple needs a website",
 * not "a digital invitation always replaces one", not "destination weddings
 * require a site". Each of those is a condition list in this article, and each
 * list is introduced as conditions rather than as a rule.
 *
 * ── The claims this guide is allowed to make about Agapaé ─────────────────
 * Verified against `api/constants/planFeatures.mjs` (invitationProduct is
 * `pro`/Heirloom only), the `GET /api/events/:eventId/public` projection in
 * `api/routes/events.routes.mjs` (exactly which fields the invitation can
 * carry), `invite/src/pages/RSVP.jsx` (which sections render) and
 * `invite/src/components/rsvp/useRsvpFlow.js` (the reply really is submitted
 * from inside the invitation). Nothing beyond:
 *
 *   - the digital invitation is available on the Heirloom plan
 *   - it is one editorial design, at its own private link, in Greek and English
 *   - it can carry the couple's photographs and story, the ceremony and venue
 *     with map links, the schedule of the day, gift information, and the RSVP
 *   - guests reply inside the invitation; replies become the couple's guest list
 *   - guest management, planning and seating are part of the Heirloom experience
 *   - guest photo sharing is part of the broader product, on every plan
 *   - the invitation can lead guests on into that wider guest experience
 *
 * And the prohibitions, which matter more here than on any earlier guide
 * because the subject invites exactly the overclaim:
 *
 *   - NOT a wedding website builder, a website template platform or a CMS
 *   - NO multiple themes or invitation templates — there is ONE design
 *   - NO custom domain and NO custom URL. The link is an auto-generated
 *     `adjective-noun-XXXX` event id (`api/utils/generators.mjs`), not a name
 *     the couple picks. "Its own private link" is the accurate phrase; anything
 *     stronger is a false claim.
 *   - NO travel, accommodation, FAQ or dress-code sections. They are not in the
 *     public payload, so the article says plainly that the invitation is a
 *     focused page rather than a reference site — which is also the honest
 *     answer to its own "when a website adds value" section.
 *   - NO "unlimited", "permanent" or "forever" anywhere. Galleries have a
 *     retention window; the subject never comes up here and must not.
 *   - NO automatic behaviour that does not exist. The couple chooses who
 *     receives the invitation and when.
 *
 * ── Structure, and the four things that were cut ──────────────────────────
 * Thirteen sections against the commission's fifteen subjects, with two
 * merges, both because the pair was one subject written twice:
 *
 *   1. "What about a digital invitation that includes RSVP?" is folded into
 *      "Where the Two Overlap" — it IS the overlap, and it carries that
 *      section's closing pivot from format to function rather than restating
 *      it under its own H2 four hundred words later.
 *   2. "Where Agapaé fits" and "From invitation to the wider experience" are
 *      one section with two sub-parts. They are a single claim — the
 *      invitation is one focused part of a wider product — and splitting them
 *      would mean making that claim twice, which reads as selling.
 *
 * The third cut is inside "The Functional Difference", which was drafted with
 * four sub-parts and ships with three: "what each one is for" was the opening
 * section's argument restated as a sub-heading, so purpose is established once,
 * where the reader first meets the distinction, and the comparison section
 * covers only what that section had not already said. The fourth is in "If You
 * Use Both", which lost a "put moving details where you can change them"
 * sub-heading to the sentence that now closes "give every piece of information
 * one home" — where a fact lives and where a CHANGING fact lives are one rule,
 * and two H3s made the section state it twice.
 *
 * The first draft ran to 3,322 rendered words against a commissioned
 * 2,000-2,700; it ships at roughly 2,720. The difference came out of prose, not
 * subjects: every mandated section, condition list, hedge and link survived the
 * pass, and the passages that went were the ones saying something a different
 * section had already said. Anything added here should replace something rather
 * than extend it.
 *
 * The closing prose section is named for what it does rather than "Where This
 * Fits in Agapaé", because the `related` block below already renders those
 * words as an H2 and two identical H2s is a defect. Same call as every earlier
 * guide.
 *
 * ── One thing to leave alone ──────────────────────────────────────────────
 * No copy in this file may contain "&", "<" or ">". The generator escapes them
 * correctly, but `guides.seo.test.mjs` compares against tag-stripped HTML
 * without decoding entities, so a string containing one is reported missing
 * from a document that in fact carries it.
 */

const weddingWebsiteVsDigitalWeddingInvitationEn = {
  slug: "wedding-website-vs-digital-wedding-invitation",

  title: "Wedding Website vs Digital Wedding Invitation: What's the Difference?",
  metaTitle:
    "Wedding Website vs Digital Wedding Invitation: What's the Difference? | Agapaé",
  metaDescription:
    "Wedding website or digital invitation? What each one does, where they overlap, when you need one or both, and how to choose the right experience for guests.",

  eyebrow: "Digital invitations",

  hero: {
    src: "/assets/images/moments/moment_1.jpg",
    // Describes the photograph, not the keyword: a black and white detail of a
    // groom's hand at his side, the wedding band visible, with bright window
    // light and a sheer curtain behind him.
    alt: "A black and white photograph of a groom's hand resting at his side, the wedding band on his finger, with bright window light and a sheer curtain behind him",
    width: 6558,
    height: 3659,
  },

  // Absent until the guide is genuinely published. Adding one means adding the
  // other, and `dateModified` may not precede `datePublished`.
  // datePublished: "YYYY-MM-DD",
  // dateModified: "YYYY-MM-DD",

  intro: [
    `Somewhere between choosing a venue and writing your guest list, two terms start appearing everywhere: "digital wedding invitation" and "wedding website".`,
    `Sometimes they are sold as different products, sometimes as the same one, and occasionally as a "wedding invitation website", which sounds like both and explains neither.`,
    `The confusion is not your fault: the words are used inconsistently across the industry, and two couples can mean different things by the same one.`,
    `Underneath the vocabulary there is a real difference. A digital invitation is usually the invitation experience itself; a wedding website is usually a broader information space about the wedding. But they overlap: modern invitations can hold far more than an invitation, and many wedding websites also collect replies.`,
    `This guide sets out what each one usually does, where the two blur, and how to decide what your own guests actually need.`,
  ],

  sections: [
    {
      heading: "Is a Wedding Website the Same as a Digital Wedding Invitation?",
      content: [
        `Usually not, although the two can overlap heavily and some products are genuinely both.`,
        `The clearest way to hold them apart is the question each one answers.`,
        `A digital wedding invitation answers: will you join us?`,
        `A wedding website answers: what do I need to know about this wedding?`,
        `An invitation is addressed to particular people and asks them for something; a website is addressed to everyone invited and gives them something. One tends to be short and tied to a moment, the other broader and revisited.`,
        `The framing breaks down at the edges, and the edges are large: an invitation carrying the schedule, a map and an RSVP is already doing part of a website's job.`,
        `So treat the names as a starting point rather than a specification: what matters is not what a product is called, but what it lets your guests know and do.`,
      ],
    },
    {
      heading: "What Is a Digital Wedding Invitation?",
      content: [
        `A digital wedding invitation is one your guests open on a screen instead of receiving on paper. You will also see it called an online wedding invitation, an e-invite or an electronic invitation; these usually mean the same thing.`,
        `Whatever the format, its job is fairly consistent:`,
        {
          type: "list",
          items: [
            `to invite the people you want with you`,
            `to establish the event: who is marrying, when and where`,
            `to carry the details a guest needs in order to decide and to arrive`,
            `to provide a way to respond, where the format supports one`,
          ],
        },
        { type: "subheading", text: "Simple digital invitations" },
        `Some are a file: an image, a PDF, a short video, a designed card sent as an attachment. They are simple, and they ask nothing of a guest beyond opening a message.`,
        `Their limits follow from being a file. Once sent the content is fixed, so a detail that changes leaves the copy in your guests' inboxes wrong. And a file cannot collect anything, so the reply has to happen elsewhere.`,
        { type: "subheading", text: "Interactive or hosted invitations" },
        `Others are a page rather than a file: your guests open a link and read a designed invitation in their browser.`,
        `Because it is hosted rather than sent, it can hold more than a card would: the schedule, a map link, gift information, photographs, sometimes the couple's story. It can usually be updated after it has gone out, and it often carries the RSVP, so reading and answering happen in one place.`,
        `This is the type that overlaps most with what other platforms call a wedding website, and why the terms became tangled.`,
      ],
    },
    {
      heading: "What Is a Wedding Website?",
      content: [
        `A wedding website is usually a broader reference destination: somewhere guests go, and go back to, for the detail around the day.`,
        `Contents vary by platform and couple, but these sections appear most often:`,
        {
          type: "list",
          items: [
            `the date, the venue and how to get there`,
            `the schedule or order of events, sometimes across several days`,
            `travel information for guests coming from elsewhere`,
            `accommodation options, and any rooms held for guests`,
            `dress code, with more explanation than a card has room for`,
            `registry or gift information`,
            `frequently asked questions`,
            `the couple's story, and often the wedding party`,
            `an RSVP page, on many platforms`,
          ],
        },
        `Some are a single long page, others several pages with their own navigation; some are open to anyone with the address, others sit behind a password.`,
        `Two characteristics matter more than the section list: it is built to be returned to, and built to be changed. That is the real reason the format exists.`,
      ],
    },
    {
      heading: "The Functional Difference",
      content: [
        `Set the names aside and the differences become practical.`,
        { type: "subheading", text: "How much information each carries" },
        `An invitation is edited down, and adding to it tends to weaken it. A website can hold far more without that cost, because each guest reads only the part they came for.`,
        { type: "subheading", text: "Where the reply happens" },
        `This varies entirely by product, and it is the detail most worth checking. A static invitation cannot collect a reply, so it has to point somewhere. A hosted invitation often collects it itself. A wedding website may have its own RSVP page, or none at all.`,
        { type: "subheading", text: "Updates, and how long each is used" },
        `Most guests read an invitation once, perhaps twice, and act on it. A website is consulted repeatedly, often weeks later, when someone is booking a flight or wondering what to wear, and it is expected to keep changing as planning goes on. That difference in use is what decides whether a wedding needs one at all.`,
      ],
    },
    {
      heading: "Where the Two Overlap",
      content: [
        `The categories are not sealed off from each other: they sit on a spectrum, and many products fall in the middle of it.`,
        `A hosted digital invitation can include event details, a schedule, map links, gift information and an RSVP — the same items that appear on most lists of what a wedding website should contain. From the other direction, some couples use their wedding website as the invitation, sending the link with a message and letting the site do the inviting.`,
        `Some platforms then sell one product under both names, which is where "wedding invitation website" comes from.`,
        `The clearest case is an invitation with the RSVP built into it: it invites, carries the details and takes the reply in one place. An invitation by name, and in part a wedding website by function.`,
        {
          type: "p",
          text: [
            `Which is why "website or invitation?" is rarely the right question. The better one is what your guest experience needs to do, and whether the reply then lives inside the invitation or on a separate `,
            { text: "RSVP page", href: "/wedding-rsvp" },
            ` is a practical choice rather than a category error.`,
          ],
        },
      ],
    },
    {
      heading: "When a Digital Invitation May Be Enough",
      content: [
        `For many weddings, one well-made invitation covers everything guests need. That is likelier when several of these are true:`,
        {
          type: "list",
          items: [
            `the celebration is one main event, or a ceremony and reception in one place on one day`,
            `most of your guests are local, or already know the area`,
            `the details that matter fit comfortably on one page`,
            `replying is the main thing you need from your guests`,
            `the logistics are straightforward: no rooms to book, no transfers to arrange`,
            `you would rather keep one guest-facing experience current than two`,
          ],
        },
        `None of this is a rule, and it depends on the kind of invitation you choose: an image sent by message cannot carry a schedule or take a reply, so "the invitation is enough" is only true when the invitation can do that work.`,
      ],
    },
    {
      heading: "When a Wedding Website May Add Value",
      content: [
        `A separate reference space earns its place when there is more to say than an invitation should carry. Common signs:`,
        {
          type: "list",
          items: [
            `the wedding is a destination wedding, or far from where guests live`,
            `guests are travelling from several countries, on different journeys`,
            `there is accommodation guests have to book themselves`,
            `the celebration runs across more than one day`,
            `the same questions keep coming: parking, transport, children, dress code`,
            `there is registry or gift information you would rather not print`,
            `details are still moving, and guests will need the current version`,
            `guests will come back to the information more than once`,
          ],
        },
        `Even then, a website is not the only answer: some couples handle the same material with a message to the guests it affects, or a shared document. The value comes from the shape of the information rather than the label on the tool — substantial, changing, looked up more than once.`,
      ],
    },
    {
      heading: "When Using Both Makes Sense",
      content: [
        `Plenty of couples reasonably use both, and the roles usually divide like this. The invitation is the moment and the ask: what arrives, what your guests read first, and where they reply. The website is the reference that follows: it holds the practical detail, it grows through planning, and it is where guests go back to.`,
        `The invitation stays short and points onward; the website carries the weight.`,
        `It is a sound arrangement, but not a free one. Two guest-facing destinations mean two places that have to stay true, and the cost of getting that wrong is a guest reading last month's ceremony time.`,
      ],
    },
    {
      heading: "Do You Actually Need Both?",
      content: [
        `A short set of questions usually settles it. Answer them about your own wedding rather than weddings in general:`,
        {
          type: "list",
          items: [
            `What do your guests need to know beyond when and where?`,
            `Is there travel or accommodation they arrange themselves?`,
            `Is this one event, or several days of celebration?`,
            `Where do you want replies to arrive, and is that one place?`,
            `Will guests check something weeks after being invited?`,
            `Do you want one focused experience, or will you maintain two?`,
          ],
        },
        `If your answers add up to a page of information, one reply destination and one event, a single well-built invitation is likely to be enough. If they add up to journeys, bookings and questions that keep coming, a place guests can return to justifies itself.`,
        `Neither answer depended on what the products were called, but on what your guests need to know, what they need to do, and how many places that should live in.`,
      ],
    },
    {
      heading: "If You Use Both, Decide Where Each Thing Lives",
      content: [
        `This is where couples with two destinations most often come unstuck, so decide it before you send anything, not after the first guest asks.`,
        { type: "subheading", text: "Give every piece of information one home" },
        `Write down what your guests need to know and put each item in one place only. Repeating a fact across two destinations is how they end up disagreeing, and the ceremony time is the one most often repeated. Where something must appear twice, decide which copy is the source. And anything still being decided belongs in the destination you can update, never only in a file already sitting in someone's inbox.`,
        { type: "subheading", text: "Keep one reply destination" },
        {
          type: "p",
          text: [
            `Replies arriving by three routes at once is the classic failure. Choose the one place a reply should go, say so wherever a guest might look, and point everything else at it. `,
            {
              text: "How to Organize Wedding RSVPs",
              href: "/guides/how-to-organize-wedding-rsvps",
            },
            ` covers what to do with them once they arrive.`,
          ],
        },
        { type: "subheading", text: "Keep the invitation focused" },
        {
          type: "p",
          text: [
            `An invitation asked to hold a travel guide stops reading like an invitation. Let it carry the essentials, the reply, and a pointer to wherever the rest lives — and, whatever else you decide, what you are asking and by when. `,
            {
              text: "When Should Wedding Guests RSVP",
              href: "/guides/when-should-wedding-guests-rsvp",
            },
            ` explains how to set a deadline that still leaves you time.`,
          ],
        },
      ],
    },
    {
      heading: "One Invitation, and What It Opens Into",
      content: [
        {
          type: "note",
          text: `Agapaé's digital wedding invitation is available on the Heirloom plan.`,
        },
        {
          type: "p",
          text: [
            `Agapaé takes the invitation-led approach rather than the website one. Your `,
            {
              text: "digital wedding invitation",
              href: "/digital-wedding-invitation",
            },
            ` is an editorial page at its own private link: your photographs and your story, your church and your venue with map links, the schedule of the day, gift information if you want it, and the RSVP at the end.`,
          ],
        },
        `It is worth being precise about what that is not. Agapaé is not a wedding website builder: there is no site to assemble page by page, no template gallery, no separate domain to maintain. One editorial design, in Greek and English, holds your details, and guests open it in the browser they already have, with no app and no account.`,
        `That focus has a limit worth knowing: the invitation is an invitation, not a travel guide. A wedding with substantial travel and accommodation logistics may still want a separate reference space for it.`,
        { type: "subheading", text: "What the invitation opens into" },
        {
          type: "p",
          text: [
            `Guests reply inside the invitation, for each person with them and each part of the celebration, and those replies become your guest list rather than a spreadsheet. The same names carry through the parts of Heirloom you work in privately: guest management, `,
            { text: "planning", href: "/wedding-planning" },
            `, and your table plan. Later, guest photo sharing brings in what everyone captured on the day, which belongs to the broader Agapaé product rather than the invitation.`,
          ],
        },
        `Not every guest touches every part of this: most see the invitation, reply, and much later share their photographs. The planning side is yours.`,
      ],
    },
    {
      heading: "A Short Decision Checklist",
      content: [
        {
          type: "subheading",
          text: "A digital invitation on its own may be enough if",
        },
        {
          type: "list",
          items: [
            `your celebration is one event, in one place, with a simple journey`,
            `the essentials fit on one page, and replying is the main thing you need`,
            `you would rather maintain one guest-facing experience than two`,
          ],
        },
        {
          type: "subheading",
          text: "A wedding website may be worth adding if",
        },
        {
          type: "list",
          items: [
            `guests are travelling far, or booking their own accommodation`,
            `the celebration runs over several days or several venues`,
            `there is detail that keeps changing, or that guests will look up later`,
          ],
        },
        { type: "subheading", text: "Consider both if" },
        {
          type: "list",
          items: [
            `there is genuinely more to say than an invitation should carry`,
            `you can name, now, which destination owns each piece of information`,
            `you are content to keep two things accurate until the day`,
          ],
        },
      ],
    },
    {
      heading: "Function Over Format",
      content: [
        `The difference worth remembering is not a vocabulary rule. An invitation asks your guests a question; a reference space answers theirs. Most products do more of one than the other, and the name on the page will not always tell you which.`,
        `So look at what a product actually lets your guests know and do, decide how many destinations your wedding honestly needs, and give the reply one clear home.`,
      ],
    },
  ],

  faqHeading: "Frequently Asked Questions",

  // Visible copy, always expanded. `faqSchema` is deliberately not set, exactly
  // as on the eleven earlier guides: Google restricts FAQ rich results to
  // government and health sites, so the markup would buy nothing while
  // committing us to a second copy of every answer.
  faq: [
    {
      q: `Is a wedding website the same as a digital wedding invitation?`,
      a: `Usually not, although they overlap and some platforms sell one product under both names. A digital invitation is normally the invitation experience itself: it invites your guests, carries the essential details and, depending on the product, takes their reply. A wedding website is normally a broader reference space guests return to for travel, accommodation and schedule detail.`,
    },
    {
      q: `Can a digital wedding invitation include RSVP?`,
      a: `Many can, but not all. A hosted invitation that opens at a link often collects the reply inside the invitation itself. One sent as an image, PDF or video cannot collect anything, so the reply has to happen elsewhere. If that matters to you, check it before choosing.`,
    },
    {
      q: `Do I need both a wedding website and a digital invitation?`,
      a: `Not necessarily. It depends on how much your guests need to know beyond when and where. One event with straightforward logistics is often served well by a single invitation carrying the details and the reply. Travel, accommodation, several days of celebration or information that keeps changing is what usually justifies a second destination.`,
    },
    {
      q: `Can a digital wedding invitation replace a wedding website?`,
      a: `Sometimes, and it depends on the invitation. A hosted invitation carrying the schedule, the venue, map links, gift information and the RSVP already covers much of what couples otherwise put on a website. What it will not usually replace is a detailed travel guide, a set of accommodation options or a long list of questions and answers.`,
    },
    {
      q: `What should we choose for a wedding with a lot of travel and accommodation information?`,
      a: `When guests are booking flights and rooms themselves they need somewhere to return to, so a reference space of some kind is usually worth having. It does not have to be a full wedding website: a message to the guests affected, or a shared document, can do the same job. Keep the invitation focused on the invitation and the reply, and point onward to wherever the rest lives.`,
    },
  ],

  related: {
    heading: "Where this fits in Agapaé",

    // The two RSVP guides this article hands off to. Both are already linked in
    // the prose at the point a reader needs them; naming them here is the
    // cluster relationship — invitation to reply to guest list — rather than
    // link volume, and stays well under the cap the test enforces.
    guides: ["how-to-organize-wedding-rsvps", "when-should-wedding-guests-rsvp"],

    product: [
      {
        href: "/digital-wedding-invitation",
        label: "Digital wedding invitation",
        blurb:
          "See the editorial invitation itself: one design at its own private link, carrying your story, your venue, the schedule of the day and the RSVP. Available on the Heirloom plan.",
      },
      {
        href: "/wedding-rsvp",
        label: "Wedding RSVP",
        blurb:
          "See how guests reply inside the invitation, person by person and moment by moment, and how those answers become the guest list you plan the day from.",
      },
    ],
  },
};

export default weddingWebsiteVsDigitalWeddingInvitationEn;
